import type { GridApiPremium, GridColumnVisibilityModel, GridDataSource } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { GRID_DEFAULT_PAGE_SIZE } from "@entrilia/shared/components/grid/gridHelper";
import { logError } from "@entrilia/shared/logging";
import type { InvestorChangeRequest } from "../../../api/types/workflowTypes";
import { getColumnDefinitions } from "./changeRequestsGridDataProvider";

interface Props {
  apiRef?: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  getDetailsPath?: (workflowId: string) => string;
  detailsNavigationState?: unknown;
  columnVisibilityModel?: GridColumnVisibilityModel;
}

const getRowId = (row: InvestorChangeRequest) => row.workflowId;

const ChangeRequestsGrid = ({
  apiRef,
  dataSource,
  getDetailsPath,
  detailsNavigationState,
  columnVisibilityModel,
}: Props) => {
  const [dataSourceError, setDataSourceError] = useState<Error>();

  const columns = useMemo(
    () => getColumnDefinitions({ getDetailsPath, detailsNavigationState }),
    [getDetailsPath, detailsNavigationState]
  );

  const handleDataSourceError = useCallback((error: Error) => {
    logError(error, "[ChangeRequestsGrid]");
    setDataSourceError(error);
  }, []);

  if (dataSourceError) {
    return <DataLoadingFailed title="Failed to load requests" />;
  }

  return (
    <DataGrid<InvestorChangeRequest>
      apiRef={apiRef}
      lazyLoading
      dataSource={dataSource}
      paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
      onDataSourceError={handleDataSourceError}
      columnVisibilityModel={columnVisibilityModel}
      columns={columns}
      getRowId={getRowId}
      noRowsText="No requests"
      disableColumnPinning
      disableColumnReorder
      disableColumnSorting
    />
  );
};

export default ChangeRequestsGrid;
